import React, { useState, useEffect, useCallback } from 'react';
import { adminApiClient } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface TempleStats {
  totalBookings: number;
  totalDonations: number;
  walletBalance: number;
  pendingWithdrawals: number;
  monthlyRevenue: number;
}

interface RecentActivity {
  id: string;
  type: 'booking' | 'donation' | 'withdrawal';
  description: string;
  amount?: number;
  date: string;
  status: string;
}

const TempleDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<TempleStats>({
    totalBookings: 0,
    totalDonations: 0,
    walletBalance: 0,
    pendingWithdrawals: 0,
    monthlyRevenue: 0
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New State for Verification & Visibility
  const [isActive, setIsActive] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<any>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('inactive');
  const [templeId, setTempleId] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch temple details (for visibility & subscription status)
      try {
        const templeRes = await adminApiClient.getMyTemple();
        if (templeRes.success && templeRes.data?.temple) {
          setIsActive(templeRes.data.temple.isActive);
          setSubscriptionStatus(templeRes.data.temple.subscriptionStatus || 'inactive');
          setTempleId(templeRes.data.temple._id);
        }
      } catch (e) {
        console.error("Failed to fetch temple details", e);
      }

      // Fetch Registration Status (for verification cards)
      if (user?.email) {
        try {
          const regRes = await adminApiClient.getUserRegistrationStatus(user.email);
          if (regRes.success && regRes.data?.registration) {
            setVerificationStatus(regRes.data.registration.verificationStatus);
          }
        } catch (e) {
          console.error("Failed to fetch registration status", e);
        }
      }

      // Fetch wallet balance
      const walletRes = await adminApiClient.getWalletBalance(user?.templeId || '');
      if (walletRes.success && walletRes.data?.wallet) {
        setStats(prev => ({
          ...prev,
          walletBalance: walletRes.data!.wallet.balance
        }));
      }

      // Fetch recent bookings
      const bookingsRes = await adminApiClient.getBookingsByTemple(user?.templeId || '', {
        page: 1,
        limit: 5
      });

      if (bookingsRes.success && bookingsRes.data?.bookings) {
        const bookings = bookingsRes.data.bookings.map((booking: any) => ({
          id: booking._id || booking.id,
          type: 'booking' as const,
          description: `New booking: ${booking.pooja?.name || 'Unknown Pooja'}`,
          amount: booking.amount,
          date: booking.createdAt,
          status: booking.status
        }));

        setRecentActivity(prev => [...prev, ...bookings]);
        setStats(prev => ({
          ...prev,
          totalBookings: bookingsRes.data!.pagination?.total || 0
        }));
      }

      // Fetch recent donations for the temple
      const donationsRes = await adminApiClient.getDonations({
        templeId: user?.templeId,
        page: 1,
        limit: 5
      });

      if (donationsRes.success && donationsRes.data?.donations) {
        const donations = donationsRes.data.donations.map((donation: any) => ({
          id: donation._id || donation.id,
          type: 'donation' as const,
          description: `Donation: ${donation.donorName || 'Anonymous'}`,
          amount: donation.amount,
          date: donation.createdAt,
          status: donation.status
        }));

        setRecentActivity(prev => [...prev, ...donations]);

        // Calculate total donations and monthly revenue
        const totalDonations = donationsRes.data.donations.reduce((sum: number, d: any) => sum + d.amount, 0);
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        const monthlyDonations = donationsRes.data.donations
          .filter((d: any) => {
            const donationDate = new Date(d.createdAt);
            return donationDate.getMonth() === currentMonth && donationDate.getFullYear() === currentYear;
          })
          .reduce((sum: number, d: any) => sum + d.amount, 0);

        setStats(prev => ({
          ...prev,
          totalDonations,
          monthlyRevenue: monthlyDonations
        }));
      }

    } catch (err: any) {
      setError(err.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, [user?.templeId, user?.email]);



  useEffect(() => {
    if (user?.templeId) {
      fetchDashboardData();
    }
  }, [user?.templeId, fetchDashboardData]);

  const handleToggleVisibility = async (newValue: boolean) => {
    if (!templeId) return;
    try {
      const res = await adminApiClient.toggleTempleVisibility(templeId, newValue);
      if (res.success) {
        setIsActive(newValue);
        toast.success(newValue ? 'Temple is now Visible' : 'Temple is now Hidden');
      } else {
        toast.error(res.message || 'Failed to update visibility');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed');
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'booking': return '📅';
      case 'donation': return '💰';
      case 'withdrawal': return '💸';
      default: return '📋';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'text-green-600';
      case 'pending': return 'text-yellow-600';
      case 'completed': return 'text-[var(--color-secondary)]';
      case 'cancelled': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner"></div>
        <p>Loading temple dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-banner">
        <span className="error-icon">⚠️</span>
        <span>{error}</span>
        <button onClick={() => setError(null)} className="error-close">×</button>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="header-left">
          <h2>Temple Dashboard</h2>
          <p>Manage your temple's activities and finances</p>
        </div>
        <button onClick={fetchDashboardData} className="btn-secondary" disabled={loading}>
          {loading ? '⏳' : '🔄'} Refresh
        </button>
      </div>

      {/* Verification & Visibility Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Verification Status */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Verification Status</h3>
          <div className="space-y-4">
            {[
              { key: 'certificate501c3', label: '501(c)(3) Status' },
              { key: 'stateVerification', label: 'State Registration' },
              { key: 'irsPropublica', label: 'IRS / ProPublica' }
            ].map((item) => {
              const status = verificationStatus?.[item.key]?.status || 'pending';
              const color = status === 'verified' ? 'bg-green-100 text-green-800' :
                status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800';
              return (
                <div key={item.key} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="font-medium text-gray-700">{item.label}</span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold capitalize ${color}`}>
                    {status}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Website Visibility */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Website Visibility</h3>
          <div className="flex flex-col h-full justify-between">
            <div>
              <p className="text-gray-600 mb-4">
                Control your temple's visibility on the public website.
              </p>
              <div className="flex items-center space-x-4 mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  Currently: {isActive ? 'Visible' : 'Hidden'}
                </span>
                {subscriptionStatus !== 'active' && (
                  <span className="text-red-500 text-xs font-medium">Subscription Inactive</span>
                )}
              </div>
            </div>

            <div className="mt-auto pt-4 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Publish to Website</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={isActive}
                    onChange={(e) => handleToggleVisibility(e.target.checked)}
                    disabled={
                      // Disable if subscription inactive OR any verification pending/rejected
                      subscriptionStatus !== 'active' ||
                      !verificationStatus ||
                      Object.values(verificationStatus).some((v: any) => v?.status !== 'verified')
                    }
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[color-mix(in_srgb,var(--color-secondary)_30%,white)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--color-secondary)]"></div>
                </label>
              </div>
              {(subscriptionStatus !== 'active' || (verificationStatus && Object.values(verificationStatus).some((v: any) => v?.status !== 'verified'))) && (
                <p className="text-xs text-red-500 mt-2">
                  * Requires Active Subscription and All Verifications Approved.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-[var(--color-secondary)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Bookings</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalBookings}</p>
            </div>
            <div className="text-3xl">📅</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Wallet Balance</p>
              <p className="text-2xl font-bold text-gray-900">${stats.walletBalance.toFixed(2)}</p>
            </div>
            <div className="text-3xl">💰</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-[var(--color-primary)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Donations</p>
              <p className="text-2xl font-bold text-gray-900">${stats.totalDonations.toFixed(2)}</p>
            </div>
            <div className="text-3xl">🎁</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
              <p className="text-2xl font-bold text-gray-900">${stats.monthlyRevenue.toFixed(2)}</p>
            </div>
            <div className="text-3xl">📈</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button
            className="btn-primary"
            onClick={() => window.location.hash = '#pooja-bookings'}
          >
            <span className="mr-2">📅</span>
            View Bookings
          </button>
          <button
            className="btn-primary"
            onClick={() => window.location.hash = '#temple-donations'}
          >
            <span className="mr-2">💰</span>
            View Donations
          </button>
          <button
            className="btn-primary"
            onClick={() => window.location.hash = '#temple-reports'}
          >
            <span className="mr-2">📊</span>
            View Reports
          </button>
          <button
            className="btn-primary"
            onClick={() => window.location.hash = '#poojas'}
          >
            <span className="mr-2">🕉️</span>
            Manage Poojas
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
        {recentActivity.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">📋</div>
            <p>No recent activity</p>
          </div>
        ) : (
          <div className="space-y-4">
            {recentActivity.slice(0, 10).map((activity) => (
              <div key={activity.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{getActivityIcon(activity.type)}</span>
                  <div>
                    <p className="font-medium text-gray-900">{activity.description}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(activity.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {activity.amount && (
                    <p className="font-semibold text-gray-900">${activity.amount}</p>
                  )}
                  <p className={`text-sm ${getStatusColor(activity.status)}`}>
                    {activity.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TempleDashboard;
